/**
 * Compact encoding of a public profile's questionnaire answers — three 36-answer sets, each answer a
 * permutation of `[0, 1, 2]`. As 108 tuples the profile cost far more than its bytes: seroval gives every
 * tuple its own backreference slot, ~340 nodes and 23% of server CPU on that page.
 *
 * Each set travels as 36 characters indexing `ANSWER_PERMUTATIONS`. Both ends share THIS module, and the
 * legacy tuple form stays accepted. Dependency-free on purpose: the entry chunk must not carry zod.
 */

/** A single answer: the candidate's ranking of three options, a permutation of `[0, 1, 2]`. */
export type AnswerTuple = [0 | 1 | 2, 0 | 1 | 2, 0 | 1 | 2]

/** The 36 answers of one questionnaire, as tuples — the form the report computation consumes. */
export type AnswerTuples = AnswerTuple[]

/**
 * The six permutations of `[0, 1, 2]` in lexicographic order. A compact character is an index into this
 * list, so the ORDER is the wire format: never reorder it.
 */
export const ANSWER_PERMUTATIONS = [
  [0, 1, 2],
  [0, 2, 1],
  [1, 0, 2],
  [1, 2, 0],
  [2, 0, 1],
  [2, 1, 0],
] as const satisfies readonly (readonly [0 | 1 | 2, 0 | 1 | 2, 0 | 1 | 2])[]

/** Answers per questionnaire; mirrors `answersSchema`'s `.length(36)` in shared.ts. */
export const ANSWERS_PER_TEST = 36

/** What a compact answer set looks like: exactly 36 characters, each an index into `ANSWER_PERMUTATIONS`. */
export const COMPACT_ANSWERS_PATTERN = /^[0-5]{36}$/

/** `'012' → 0`, `'021' → 1`, … — the inverse of `ANSWER_PERMUTATIONS`, keyed by the tuple's digits. */
const PERMUTATION_INDEX: Record<string, number> = Object.fromEntries(
  ANSWER_PERMUTATIONS.map((permutation, index) => [permutation.join(''), index]),
)

/** True for a well-formed compact answer set. A plain type guard — no parse, no allocation. */
export function isCompactAnswers(value: unknown): value is string {
  return typeof value === 'string' && COMPACT_ANSWERS_PATTERN.test(value)
}

/**
 * Encode 36 answer tuples as a 36-character string. Throws on anything that is not exactly 36
 * permutations of `[0, 1, 2]` — the callers (a validated schema arm, the generator form) already hold
 * a checked value, so a throw here is a bug, not user input.
 */
export function encodeAnswers(answers: readonly (readonly number[])[]): string {
  if (answers.length !== ANSWERS_PER_TEST) {
    throw new Error(`Expected ${ANSWERS_PER_TEST} answers, got ${answers.length}`)
  }
  let compact = ''
  for (let i = 0; i < answers.length; i++) {
    const answer = answers[i]
    const index = answer?.length === 3 ? PERMUTATION_INDEX[answer.join('')] : undefined
    if (index === undefined) throw new Error(`Answer ${i} is not a permutation of [0, 1, 2]`)
    compact += index
  }
  return compact
}

/**
 * Decode a compact answer set back to 36 tuples. Throws when the string is not `COMPACT_ANSWERS_PATTERN`.
 * Every tuple is a fresh array: the report computation is free to hold on to them.
 */
export function decodeAnswers(compact: string): AnswerTuples {
  if (!COMPACT_ANSWERS_PATTERN.test(compact)) {
    throw new Error(`Expected ${ANSWERS_PER_TEST} characters in the range 0-5, got ${JSON.stringify(compact)}`)
  }
  const answers: AnswerTuples = []
  for (let i = 0; i < compact.length; i++) {
    // The pattern test above guarantees the index is one of the six; the fallback only satisfies the
    // indexed-access type and never runs.
    const permutation = ANSWER_PERMUTATIONS[Number(compact[i])] ?? ANSWER_PERMUTATIONS[0]
    answers.push([permutation[0], permutation[1], permutation[2]])
  }
  return answers
}

/**
 * Normalise either form to the compact string. Used where a value that may still be the legacy tuple
 * form (an old shared link, the generator's pasted JSON) needs to become the wire form.
 */
export function toCompactAnswers(value: string | readonly (readonly number[])[]): string {
  if (typeof value === 'string') {
    if (!COMPACT_ANSWERS_PATTERN.test(value)) {
      throw new Error(`Expected ${ANSWERS_PER_TEST} characters in the range 0-5, got ${JSON.stringify(value)}`)
    }
    return value
  }
  return encodeAnswers(value)
}
